<template>
  <div class="space-y-6">
    <div class="flex items-center justify-center gap-3" v-show="!browserIsIE">
      <div class="text-right text-stone-700 dark:text-stone-300">
        {{ t('optionsdialog.One color') }}
      </div>

      <div>
        <img src="/images/color-wheel.png" class="wheel-type-image" />
      </div>

      <label class="relative inline-flex items-center cursor-pointer" role="switch" :aria-checked="isImageWheel">
        <input type="checkbox" v-model="isImageWheel" class="sr-only peer" />
        <div
          class="w-11 h-6 bg-stone-300 peer-focus:outline-none rounded-full peer dark:bg-stone-600 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-stone-600 peer-checked:bg-primary-500">
        </div>
      </label>

      <div>
        <img src="/images/chocolate-chip-cookie.jpg" class="wheel-type-image" />
      </div>

      <div class="text-stone-700 dark:text-stone-300">
        {{ t('optionsdialog.Wheel background image') }}
      </div>
    </div>

    <div v-show="wheelConfig.type == 'color'" class="space-y-6">
      <div v-if="wheelConfig.isAdvanced"
        class="p-3 bg-yellow-100 dark:bg-yellow-900 rounded border border-yellow-300 dark:border-yellow-600">
        <p class="text-yellow-800 dark:text-yellow-200 text-sm">
          {{
            t('optionsdialog.Advanced mode is enabled') ||
            'Advanced mode is enabled. Disable it from the text editor to apply a theme.'
          }}
        </p>
        <button @click="disableAdvancedMode"
          class="mt-2 px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded text-sm">
          {{ t('textboxbuttons.Revert from advanced') || 'Disable advanced mode' }}
        </button>
      </div>

      <div v-if="!wheelConfig.isAdvanced"
        class="rounded-md border border-stone-200 dark:border-stone-700 bg-white/60 dark:bg-stone-800/40 p-3">
        <h3 class="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
          {{ t('optionsdialog.Pick theme') }}
        </h3>
        <colorThemeSelector :disabled="wheelConfig.isAdvanced" @set-color-theme="setColorTheme" />
      </div>

      <div class="rounded-md border border-stone-200 dark:border-stone-700 bg-white/60 dark:bg-stone-800/40 p-3">
        <div class="flex items-center text-stone-700 dark:text-stone-300 mb-2">
          {{ t('optionsdialog.Customize colors') }}
          <span class="ml-1">
            <tooltip :title="t('optionsdialog.Colors tooltip')" />
          </span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <div v-for="(setting, index) in wheelConfig.colorSettings" :key="index" class="flex items-center gap-2">
            <input :id="'colorSettingEnabled-' + index" type="checkbox" v-model="setting.enabled"
              :disabled="wheelConfig.isAdvanced"
              class="form-checkbox h-4 w-4 text-primary-500 rounded focus:ring-primary-500 border-stone-300 dark:border-stone-600 disabled:opacity-50" />
            <label class="text-sm text-stone-700 dark:text-stone-300" :for="'colorSettingEnabled-' + index">
              {{ t('optionsdialog.Color') || 'Color' }} {{ index + 1 }}
            </label>
            <input :aria-labelledby="'colorSettingEnabled-' + index" type="color" v-show="setting.enabled"
              v-model="setting.color" :disabled="wheelConfig.isAdvanced"
              class="h-8 w-8 rounded border-0 cursor-pointer disabled:opacity-50 ml-auto" />
          </div>
        </div>
      </div>

      <div class="grid md:grid-cols-2 gap-4">
        <div class="rounded-md border border-stone-200 dark:border-stone-700 bg-white/60 dark:bg-stone-800/40 p-3">
          <wheelCenterImagePicker :imageSrc="centerImageSrc" :pictureType="wheelConfig.pictureType"
            :galleryPicture="wheelConfig.galleryPicture" :customPictureName="wheelConfig.customPictureName"
            :centerText="wheelConfig.centerText" :isAdvanced="wheelConfig.isAdvanced"
            @set-center-no-image="setCenterNoImage" @set-center-gallery-image="setCenterGalleryImage"
            @set-center-text="setCenterText" @set-center-uploaded-image="setCenterUploadedImage"
            @set-color-values="setColorValues" v-show="!browserIsIE" />
        </div>
        <div class="rounded-md border border-stone-200 dark:border-stone-700 bg-white/60 dark:bg-stone-800/40 p-3">
          <label for="hubSize" class="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
            {{ t('optionsdialog.Image size') }}
          </label>
          <select id="hubSize" v-model="wheelConfig.hubSize"
            class="w-full bg-white dark:bg-stone-700 border border-stone-300 dark:border-stone-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-stone-700 dark:text-stone-200">
            <option v-for="hubSize in hubSizes" :value="hubSize" :key="hubSize">
              {{ hubSize }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div v-show="wheelConfig.type == 'image'" class="space-y-6">
      <div class="rounded-md border border-stone-200 dark:border-stone-700 bg-white/60 dark:bg-stone-800/40 p-3"
        v-show="!browserIsIE">
        <wheelBackgroundImagePicker :imageSrc="backgroundImageSrc"
          @set-background-gallery-image="setBackgroundGalleryImage"
          @set-background-uploaded-image="setBackgroundUploadedImage" />
      </div>
    </div>

    <div class="rounded-md border border-stone-200 dark:border-stone-700 bg-white/60 dark:bg-stone-800/40 p-3">
      <label class="inline-flex items-center">
        <input id="drawOutlines" type="checkbox" v-model="wheelConfig.drawOutlines"
          class="form-checkbox h-4 w-4 text-primary-500 rounded focus:ring-primary-500 border-stone-300 dark:border-stone-600" />
        <span class="ml-2 text-stone-700 dark:text-stone-300">
          {{ t('optionsdialog.Contours') }}
        </span>
      </label>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWheelStore } from '@/stores/useWheelStore';
import tooltip from '@/components/ui/tooltip.vue';
import colorThemeSelector from '@/components/ui/colorThemeSelector.vue';
import wheelBackgroundImagePicker from '@/components/wheel/wheelBackgroundImagePicker.vue';
import wheelCenterImagePicker from '@/components/wheel/wheelCenterImagePicker.vue';
import * as ImageUtil from '@utils/ImageUtil.js';
import * as Util from '@utils/Util.js';
import * as hubSizesMod from '@/components/wheel/hubSizes.js';

const props = defineProps({
  wheelConfig: { type: Object, required: true }
});

const emit = defineEmits(['theme-selected']);

const { t } = useI18n();
const wheelStore = useWheelStore();
const instance = getCurrentInstance();

const hubSizes = Object.keys(hubSizesMod.hubSizes);
const browserIsIE = Util.browserIsIE(navigator.userAgent);

const isImageWheel = computed({
  get: () => props.wheelConfig.type == 'image',
  set: (newValue) => { props.wheelConfig.type = newValue ? 'image' : 'color'; }
});

const backgroundImageSrc = computed(() => {
  if (props.wheelConfig.coverImageType == 'gallery') return props.wheelConfig.coverImageName;
  if (props.wheelConfig.coverImageType == 'uploaded') return props.wheelConfig.customCoverImageDataUri;
  return '/images/none.png';
});

const centerImageSrc = computed(() => {
  if (props.wheelConfig.getWheelImage) return props.wheelConfig.getWheelImage();
});

const setCenterNoImage = () => {
  props.wheelConfig.pictureType = 'none';
  props.wheelConfig.galleryPicture = '/images/none.png';
  props.wheelConfig.customPictureName = '';
  props.wheelConfig.customPictureDataUri = '';
  props.wheelConfig.centerText = '';
};

const setCenterGalleryImage = (fileName) => {
  props.wheelConfig.pictureType = 'gallery';
  props.wheelConfig.galleryPicture = fileName;
  props.wheelConfig.customPictureName = '';
  props.wheelConfig.customPictureDataUri = '';
  props.wheelConfig.centerText = '';
};

const setCenterText = (text) => {
  props.wheelConfig.pictureType = 'text';
  props.wheelConfig.galleryPicture = '/images/none.png';
  props.wheelConfig.customPictureName = '';
  props.wheelConfig.customPictureDataUri = '';
  props.wheelConfig.centerText = text;
  props.wheelConfig.customPictureDataUri = ImageUtil.renderText(text);
};

const setCenterUploadedImage = ({ fileName, dataUri }) => {
  props.wheelConfig.pictureType = 'uploaded';
  props.wheelConfig.customPictureName = fileName;
  props.wheelConfig.customPictureDataUri = dataUri;
  props.wheelConfig.centerText = '';
};

const setBackgroundGalleryImage = (fileName) => {
  props.wheelConfig.coverImageType = 'gallery';
  props.wheelConfig.coverImageName = fileName;
  props.wheelConfig.customCoverImageDataUri = '';
};

const setBackgroundUploadedImage = ({ fileName, dataUri }) => {
  props.wheelConfig.coverImageName = fileName;
  props.wheelConfig.customCoverImageDataUri = dataUri;
  props.wheelConfig.coverImageType = 'uploaded';
};

const setColorTheme = (payload) => {
  try { emit('theme-selected', payload?.name || ''); } catch { }
  props.wheelConfig.setColorValues(payload.colors);
};

const setColorValues = (colors) => {
  props.wheelConfig.setColorValues(colors);
};

const disableAdvancedMode = async () => {
  const ui = instance?.appContext.config.globalProperties.$ui || window.$ui;
  const confirmed = await ui?.dialog.confirm({
    title: t('textboxbuttons.Revert from advanced') || 'Disable Advanced Mode',
    message: t('textboxbuttons.Reverting will reset') || 'Disabling advanced mode will reset individual slice colors. Continue?',
    cancelText: t('common.Cancel'),
    confirmText: t('common.Continue')
  });
  if (confirmed) {
    await wheelStore.setAdvanced(false);
    Util.trackEvent('Wheel', 'RevertFromAdvanced');
    // Refresh from store to reflect defaults
    const fresh = wheelStore.wheelConfig.clone();
    Object.assign(props.wheelConfig, fresh);
  }
};
</script>

<style scoped>
.wheel-type-image {
  height: 50px;
  border-radius: 50%;
  filter: drop-shadow(2px 2px 2px #aaaaaa);
}
</style>
